import { AppLayout, Button, Header, HelpPanel, Tabs } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { AmbassadorDirectoryTable } from '../components/ambassadors/ambassadorDirectoryTable';
import { useMemo, useState } from 'react';
import { AmbassadorDirectoryDosAndDonts } from '../components/ambassadors/ambassadorDirectoryDosAndDonts';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG } from '../utils/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetAmbassadors } from '../hooks/ambassadors/getAmbassadors';
import { useUser } from '../hooks/user';
import { AmbassadorRecord } from '@amzn/red-velvet-api';
import { useCustomSearchParams } from '../hooks/useCustomSearchParams';

export function AmbassadorDirectory() {
  const [dosAndDontsOpen, setDosAndDontsOpen] = useState(false);
  const { t } = useTranslation(['ambassadors', 'translation']);
  const ambassadors = useGetAmbassadors();
  const loggedInUser = useUser();
  const navigate = useNavigate();
  const { searchParams, addSearchParam, deleteSearchParam } = useCustomSearchParams();

  const [activeTabId, setActiveTabId] = useState(
    searchParams.get('in-your-area') === 'true' ? 'area' : 'global'
  );

  const registerOrOffboardButton = useMemo(() => {
    const currentUserIsAnAmbassador = ambassadors.data?.find(
      (item: AmbassadorRecord) => item.employeeId === loggedInUser.employeeId
    );
    if (!currentUserIsAnAmbassador) {
      return (
        <Button onClick={() => navigate('/ambassadors/register')}>
          {t('ambassadorDirectory.becomeAnAmbassador')}
        </Button>
      );
    } else {
      return (
        <Button
          data-testid="ambassador-directory-update-button"
          onClick={() => addSearchParam('update', 'true')}
        >
          {t('ambassadorDirectory.manageDetails')}
        </Button>
      );
    }
  }, [ambassadors.data, loggedInUser.employeeId, t, navigate, addSearchParam]);

  return (
    <AppLayout
      navigationHide
      toolsHide={!dosAndDontsOpen}
      toolsOpen={dosAndDontsOpen}
      onToolsChange={() => setDosAndDontsOpen((d) => !d)}
      tools={
        <HelpPanel header={<h2>{t('ambassadorDirectory.dosAndDonts')}</h2>}>
          <AmbassadorDirectoryDosAndDonts />
        </HelpPanel>
      }
      content={
        <div>
          <Header
            variant="h1"
            info={
              <Button
                data-directory-dos-and-donts
                onClick={() => setDosAndDontsOpen((d) => !d)}
                variant="inline-link"
              >
                {t('ambassadorDirectory.dosAndDonts')}
              </Button>
            }
            actions={<>{!ambassadors.isLoading && registerOrOffboardButton}</>}
            data-testid="ambassador-directory-header"
          >
            {t('ambassadorDirectory.title')}
          </Header>
          <Tabs
            onChange={({ detail }) => {
              if (detail.activeTabId === 'global') {
                deleteSearchParam('in-your-area');
              } else {
                addSearchParam('in-your-area', 'true');
              }
              setActiveTabId(detail.activeTabId);
            }}
            activeTabId={activeTabId}
            tabs={[
              {
                label: t('ambassadorDirectory.global'),
                id: 'global',
                content: (
                  <AmbassadorDirectoryTable
                    ambassadors={ambassadors.data || []}
                    isLoading={ambassadors.isLoading}
                  />
                ),
              },
              {
                label: t('ambassadorDirectory.inYourArea'),
                id: 'area',
                content: (
                  <>
                    <AmbassadorDirectoryTable
                      ambassadors={ambassadors.data || []}
                      isLoading={ambassadors.isLoading}
                      inYourArea
                    />
                  </>
                ),
              },
            ]}
          />
        </div>
      }
    />
  );
}

export function AmbassadorDirectoryView() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  if (featureIsEnabled(COMMUNITY_AMBASSADOR_FEATURE_FLAG, features, searchParams)) {
    return <AmbassadorDirectory />;
  } else {
    return <></>;
  }
}
